import { observable, action } from 'mobx';
import { MCP_OAUTH_BROADCAST_CHANNEL } from '@kentik/ui-shared/kaizen/constants';
import { MCP_BUILTIN_SERVERS } from '@kentik/ui-shared/kaizen/mcpBuiltinServers';
import api from 'core/util/api';
import { openMcpOAuthPopup } from 'app/util/mcpOAuthPopup';

const baseUrl = (key) => `/api/ui/ai/builtin-mcp/${key}`;

/**
 * Built-in MCP integrations (e.g. Infoblox): an Admin switches one on for the company and
 * every user connects their own provider account. Keyed by built-in key.
 */
class BuiltinMcpServerStore {
  @observable stateMap = new Map();

  @observable loadingMap = new Map();

  @observable connectionMap = new Map();

  @observable loadErrorMap = new Map();

  // A sign-in completed in any tab changes this user's grant; its message names the row, not the key.
  constructor() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.oauthChannel = new BroadcastChannel(MCP_OAUTH_BROADCAST_CHANNEL);
      this.oauthChannel.unref?.();
      this.oauthChannel.onmessage = ({ data }) => {
        if (data?.type === 'mcp-oauth-complete') {
          Array.from(this.stateMap.keys()).forEach((key) => this.load(key));
        }
      };
    }
  }

  @action
  _setState = (key, state) => {
    this.stateMap.set(key, state);
  };

  @action
  _setLoading = (key, loading) => {
    this.loadingMap.set(key, loading);
  };

  @action
  _setLoadError = (key, error) => {
    this.loadErrorMap.set(key, error);
  };

  @action
  _setConnection = (key, connection) => {
    this.connectionMap.set(key, { ...(this.connectionMap.get(key) || {}), ...connection });
  };

  // The server says this user's grant no longer works, whatever the last load or test said.
  @action
  _markSignedOut = (key, error = null) => {
    const state = this.stateMap.get(key);

    if (state) {
      this.stateMap.set(key, { ...state, connected: false });
    }

    this._setConnection(key, { testing: false, authorizeUrl: null, result: null, error });
  };

  // A test that reached the server but failed is shown like any other error.
  _applyTestResult = (key, result) => {
    if (result.connected) {
      this._setConnection(key, { testing: false, authorizeUrl: null, result, error: null });
    } else {
      this._setConnection(key, { testing: false, authorizeUrl: null, result: null, error: result.error });
    }

    return this.load(key).then(() => result);
  };

  /** The chat reported this built-in needs a sign-in, so a connection made earlier in the session is gone. */
  markAuthRequired = (key) => this._markSignedOut(key);

  getState = (key) => this.stateMap.get(key);

  getConnection = (key) => this.connectionMap.get(key) || {};

  /** This user's connection in the shape the chat's connect card reads for any MCP server. */
  getOAuthStatus = (key) => {
    const connection = this.getConnection(key);

    return {
      connected: Boolean(this.getState(key)?.connected || connection.result?.connected),
      connecting: Boolean(connection.testing),
      authorizeUrl: connection.authorizeUrl ?? null,
      error: connection.error ?? null
    };
  };

  isLoading = (key) => !!this.loadingMap.get(key);

  getLoadError = (key) => this.loadErrorMap.get(key) ?? null;

  // A failed load is kept as an error, so the page cannot pass it off as the integration being off.
  load = (key) => {
    this._setLoading(key, true);

    return api
      .get(baseUrl(key))
      .then((state) => {
        this._setState(key, state);
        this._setLoadError(key, null);
      })
      .catch((err) => this._setLoadError(key, err.message || 'Could not load the integration'))
      .finally(() => this._setLoading(key, false));
  };

  setEnabled = (key, enabled) =>
    api.put(baseUrl(key), { data: { enabled } }).then((state) => {
      this._setState(key, state);
      this._setConnection(key, { result: null, error: null });

      return state;
    });

  /** Moving to another realm signs every user out, so this user's last test result no longer holds. */
  setRealm = (key, realm) =>
    api.put(baseUrl(key), { data: { realm } }).then((state) => {
      this._setState(key, state);
      this._setConnection(key, { result: null, error: null });

      return state;
    });

  /**
   * initialize + tools/list with the current user's grant. Without a working grant the user has to
   * click Connect: a popup opened here, after the request, would be blocked by the browser.
   */
  test = (key) => {
    this._setConnection(key, { testing: true, error: null });

    return api
      .post(`${baseUrl(key)}/test`, { data: {} })
      .then((result) => {
        if (result.auth_required) {
          this._markSignedOut(key, 'Your sign-in has expired. Connect again to keep using it.');
          return result;
        }

        return this._applyTestResult(key, result);
      })
      .catch((err) => {
        this._setConnection(key, { testing: false, result: null, error: err.message || 'Test failed' });
      });
  };

  /** Revokes only this user's grant; the integration stays on for everyone else. */
  disconnect = (key) => {
    this._setConnection(key, { disconnecting: true, error: null });

    return api
      .del(`${baseUrl(key)}/oauth/disconnect`)
      .then(() => {
        this._setConnection(key, { disconnecting: false, result: null });

        return this.load(key);
      })
      .catch((err) => {
        this._setConnection(key, { disconnecting: false, error: err.message || 'Disconnect failed' });
      });
  };

  /**
   * The full flow: provider sign-in in a new window, then the same test as above. Started from a
   * chat call card, `pause` binds the sign-in to that call so the answer resumes once it completes.
   */
  connect = (key, { pause } = {}) => {
    this._setConnection(key, { testing: true, error: null, authorizeUrl: null });

    return openMcpOAuthPopup(key, {
      pause,
      authorizePath: `${baseUrl(key)}/oauth/authorize`,
      onBlocked: (url) => this._setConnection(key, { authorizeUrl: url })
    })
      .then(() => api.post(`${baseUrl(key)}/test`, { data: {} }))
      .then((result) => {
        const name = MCP_BUILTIN_SERVERS[key].displayName;

        return this._applyTestResult(
          key,
          result.auth_required
            ? {
                connected: false,
                error: `${name} did not accept the sign-in. Check ${name} portal permissions for this user.`
              }
            : result
        );
      })
      .catch((err) => {
        this._setConnection(key, {
          testing: false,
          authorizeUrl: null,
          result: null,
          error: err.cancelled ? 'Authentication was not completed.' : err.message || 'Authentication failed'
        });

        return this.load(key);
      });
  };
}

export default new BuiltinMcpServerStore();
